import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose, MdSearch } from 'react-icons/md';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const CustomerSettings = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [agents, setAgents] = useState([]);
  const [formData, setFormData] = useState({
    cutomer_status: true,
    customer_name: '',
    customer_address: '',
    customer_phone: '',
    customer_contact: '',
    customer_contact_email: '',
    customer_contact_phone: '',
    customer_account_manager: '',
    company_id: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setFormData(prev => ({
        ...prev,
        company_id: userData.companyId
      }));
      fetchCustomers(userData.companyId);
      fetchAgents(userData.companyId);
    }
  }, []);

  useEffect(() => {
    if (showForm) {
      const timer = setTimeout(() => {
        const input = document.querySelector('input[name="customer_name"]');
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showForm]);

  const fetchCustomers = async (companyId) => {
    try {
      const response = await api.get(`/customers/${companyId}`);
      if (response.data.success) {
        setCustomers(response.data.customers);
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
      showPopup({
        title: t('common.error'),
        message: t('customerSettings.loadError'),
        type: 'error',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async (companyId) => {
    try {
      const response = await api.get(`/agents/company/${companyId}`);
      if (response.data.success) {
        setAgents(response.data.agents);
      }
    } catch (error) {
      console.error('Agentlar yüklenirken hata:', error);
    }
  };

  const handleDelete = async (customerId, customerName) => {
    showPopup({
      title: t('customerSettings.deleteCustomer'),
      message: t('customerSettings.deleteConfirm', { name: customerName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/customers/${customerId}`);
          if (response.data.success) {
            setCustomers(customers.filter(customer => customer.id !== customerId));
            showPopup({
              title: t('common.success'),
              message: t('customerSettings.customerDeleted', { name: customerName }),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('customerSettings.deleteError'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };

  const handleEdit = (customer) => {
    setFormData({
      cutomer_status: customer.cutomer_status === 1,
      customer_name: customer.customer_name,
      customer_address: customer.customer_address,
      customer_phone: customer.customer_phone,
      customer_contact: customer.customer_contact,
      customer_contact_email: customer.customer_contact_email,
      customer_contact_phone: customer.customer_contact_phone,
      customer_account_manager: customer.customer_account_manager || '',
      company_id: customer.company_id
    });
    setIsEditing(true);
    setEditingId(customer.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_id) {
      showPopup({
        title: t('common.error'),
        message: t('customerSettings.companyNotFound'),
        type: 'error',
        showCancel: false
      });
      return;
    }

    try {
      let response;
      const submitData = { ...formData };
      
      if (isEditing) {
        response = await api.put(`/customers/${editingId}`, submitData);
      } else {
        response = await api.post('/customers', submitData);
      }

      if (response.data.success) {
        // Form'u hemen kapat ve verileri sıfırla
        setShowForm(false);
        setIsEditing(false);
        setEditingId(null);
        const userData = JSON.parse(localStorage.getItem('user'));
        fetchCustomers(userData.companyId);
        setFormData(prev => ({
          cutomer_status: true,
          customer_name: '',
          customer_address: '',
          customer_phone: '',
          customer_contact: '',
          customer_contact_email: '',
          customer_contact_phone: '',
          customer_account_manager: '',
          company_id: prev.company_id
        }));
        
        // Başarı popup'ını göster
        showPopup({
          title: t('common.success'),
          message: isEditing 
            ? t('customerSettings.customerUpdated')
            : t('customerSettings.customerCreated'),
          type: 'success',
          showCancel: false,
          onConfirm: closePopup
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
      } else {
        showPopup({
          title: t('common.error'),
          message: error.response?.data?.message || t('customerSettings.operationError'),
          type: 'error',
          showCancel: false,
          onConfirm: closePopup
        });
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              value
    }));
  };

  // Popup yönetimi için fonksiyonlar
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Sayfa boyutu seçenekleri
  const pageSizeOptions = [10, 20, 50, 100, 500];

  // Sayfa boyutunu değiştirme fonksiyonu
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değişince ilk sayfaya dön
  };

  // Filtrelenmiş müşteriler
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = searchTerm === '' || 
      (customer.customer_name && customer.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.customer_contact && customer.customer_contact.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.customer_contact_email && customer.customer_contact_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.customer_phone && customer.customer_phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.customer_address && customer.customer_address.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  // Sayfalama hesaplamaları güncellendi
  const indexOfLastCustomer = currentPage * itemsPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Müşteri satırına tıklandığında detay sayfasına yönlendir
  const handleCustomerClick = (customer) => {
    // UUID varsa UUID kullan, yoksa ID kullan (geriye uyumluluk için)
    const identifier = customer.customer_uuid || customer.id;
    navigate(`/settings/customer/${identifier}`);
  };

  return (
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-white">{t('settings.customerSettings')}</h1>
        <button
          onClick={() => {
            // Form verilerini temizle
            setFormData(prev => ({
              cutomer_status: true,
              customer_name: '',
              customer_address: '',
              customer_phone: '',
              customer_contact: '',
              customer_contact_email: '',
              customer_contact_phone: '',
              customer_account_manager: '',
              company_id: prev.company_id
            }));
            setIsEditing(false);
            setEditingId(null);
            setShowForm(true);
          }}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] transition-colors w-11 h-10 sm:w-auto sm:h-auto sm:px-6 sm:py-3 -ml-2.5 sm:ml-0"
        >
          <MdAdd size={20} className="sm:hidden" />
          <MdAdd size={24} className="hidden sm:block" />
          <span className="hidden sm:inline">{t('customerSettings.addNewCustomer')}</span>
        </button>
      </div>

      {/* Total Count */}
      <div className="mb-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('customerSettings.totalCustomers', { count: filteredCustomers.length })}
        </span>
      </div>

      {/* Customers Table */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
        {/* Search ve Sayfa boyutu seçici */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between rounded-tl-lg rounded-tr-lg">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Müşteri ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <MdSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500" size={20} />
          </div>

          {/* Sayfa boyutu seçici */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('common.show')}</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.entries')}</span>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[600px] table-auto">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerSettings.status')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerSettings.customerName')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerSettings.contactPerson')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('customerSettings.contactEmail')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Account Manager</th>

                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {currentCustomers.length > 0 ? (
                currentCustomers.map((customer, index) => {
                  const isLast = index === currentCustomers.length - 1 && totalPages <= 1;
                  return (
                    <tr 
                      key={customer.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer`}
                      onClick={() => handleCustomerClick(customer)}
                    >
                      {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                        <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                          customer.cutomer_status 
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        }`}>
                          {customer.cutomer_status ? t('customerSettings.active') : t('customerSettings.inactive')}
                        </span>
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white`}>
                        {customer.customer_name}
                      </td>
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white`}>{customer.customer_contact}</td>
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white`}>{customer.customer_contact_email}</td>
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white`}>{customer.customer_account_manager || '-'}</td>
                      {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right font-medium ${isLast ? 'rounded-br-lg' : ''}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Satır tıklamasını engelle
                            handleEdit(customer);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          <MdEdit size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Satır tıklamasını engelle
                            handleDelete(customer.id, customer.customer_name);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <MdDelete size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-8 rounded-bl-lg rounded-br-lg">
                    <div className="flex justify-center items-center">
                      <p className="text-gray-400 dark:text-gray-500 text-sm">{t('customerSettings.noCustomers')}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4 p-4">
          {currentCustomers.length > 0 ? (
            currentCustomers.map((customer, index) => (
              <div 
                key={customer.id} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => handleCustomerClick(customer)}
              >
                {/* Status & Customer Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerSettings.status')}</div>
                    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                      customer.cutomer_status 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                    }`}>
                      {customer.cutomer_status ? t('customerSettings.active') : t('customerSettings.inactive')}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerSettings.customerName')}</div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium">{customer.customer_name}</div>
                  </div>
                </div>

                {/* Contact Person & Contact Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerSettings.contactPerson')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{customer.customer_contact}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('customerSettings.contactEmail')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{customer.customer_contact_email}</div>
                  </div>
                </div>

                {/* Account Manager */}
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Account Manager</div>
                  <div className="text-sm text-gray-900 dark:text-white">{customer.customer_account_manager || '-'}</div>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('table.actions')}</div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(customer);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                      >
                        <MdEdit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(customer.id, customer.customer_name);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <MdDelete size={16} />
                      </button>
                    </div>
                  </div>
                  <div></div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 dark:text-gray-500 text-sm">{t('customerSettings.noCustomers')}</p>
            </div>
          )}
        </div>

        {/* Pagination - Alt padding artırıldı */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('table.showing')}{' '}
                  <span className="font-medium">{indexOfFirstCustomer + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastCustomer, customers.length)}
                  </span>{' '}
                  {t('table.of')}{' '}
                  <span className="font-medium">{customers.length}</span>{' '}
                  {t('customerSettings.customers')}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.previous')}
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                        currentPage === index + 1
                          ? 'bg-[#272c39] text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('table.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}


      </div>

      {/* Popup - z-index'i artırıldı */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-medium">
                {isEditing ? t('customerSettings.editCustomer') : t('customerSettings.addNewCustomer')}
              </h3>
              <button 
                onClick={() => setShowForm(false)} 
                className="text-white hover:text-gray-200"
              >
                <MdClose size={20} />
              </button>
            </div>
            
            <div className="p-3 sm:p-6 lg:p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Status */}
              <div className="col-span-1 sm:col-span-2 flex items-center">
                <input
                  type="checkbox"
                  id="cutomer_status"
                  name="cutomer_status"
                  checked={formData.cutomer_status}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor="cutomer_status" className="ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  {t('customerSettings.active')}
                </label>
              </div>

              {/* Customer Name */}
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customerSettings.customerName')}
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              {/* Contact Person */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customerSettings.contactPerson')}
                </label>
                <input
                  type="text"
                  name="customer_contact"
                  value={formData.customer_contact}
                  onChange={handleChange}
                  autoComplete="new-contact"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customerSettings.contactEmail')}
                </label>
                <input
                  type="email"
                  name="customer_contact_email"
                  value={formData.customer_contact_email}
                  onChange={handleChange}
                  autoComplete="new-email"
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customerSettings.contactPhone')}
                </label>
                <InputMask
                  mask="(999) 999-9999"
                  type="tel"
                  name="customer_contact_phone"
                  value={formData.customer_contact_phone}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="(555) 555-5555"
                />
              </div>

              {/* Company Phone */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customerSettings.companyPhone')}
                </label>
                <InputMask
                  mask="(999) 999-9999"
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="(555) 555-5555"
                />
              </div>

              {/* Account Manager */}
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account Manager
                </label>
                <select
                  name="customer_account_manager"
                  value={formData.customer_account_manager}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Account Manager Seçin</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.full_name}>
                      {agent.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Address */}
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('customerSettings.address')}
                </label>
                <textarea
                  name="customer_address"
                  value={formData.customer_address}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Form Buttons */}
              <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
                >
                  {isEditing ? t('customerSettings.updateCustomer') : t('customerSettings.addCustomer')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSettings; 